import React, { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import AudioService from '../services/AudioService';
import { logger } from '../services/utils';
import { useStory, useStoryData, useStoryPage, useReadAloudStory, useStoryActions } from './StoryContext';
import services from '../services';

/**
 * Context for managing the global read-aloud state across the application
 */
export const ReadAloudContext = createContext({
  isPlaying: false,                // Play/pause state
  currentPageIndex: 0,             // Page being read
  readAloudStoryId: null,          // ID of the story being read aloud
  readAloudStory: null,            // The story data being read aloud
  startReading: () => {},          // Start reading from specified story and page
  startReadingCurrentStory: () => {}, // High-level method to read the current story
  unloadReading: () => {},         // Stop and unload
  togglePlayPause: () => {},       // Play/pause toggle
  isReadingActive: () => false,    // Check if reading is active
  loadStoryForReadAloud: () => Promise.resolve(false), // Load a story specifically for read-aloud
});

/**
 * Hook to use the ReadAloudContext
 * @returns {object} The ReadAloud context value
 */
export const useReadAloud = () => useContext(ReadAloudContext);

/**
 * Provider component for ReadAloudContext
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Child components
 */
export const ReadAloudProvider = ({ children }) => {

  // -------------------------------------------------------------------
  // Convenience helper so we can replace the ad‑hoc console.log calls
  // that were sprinkled throughout the file with a single, centralised
  // logger.debug invocation.  In production these are stripped by our
  // logger wrapper, keeping the console clean while retaining rich
  // diagnostics during development.
  // -------------------------------------------------------------------
  const debug = (msg, ...rest) => logger.debug('ReadAloudContext', msg, ...rest);
  // Access the read-aloud story data from StoryContext
  const { 
    story: readAloudStoryData, 
    loadStory: loadStoryFromContext 
  } = useReadAloudStory();
  
  // Access the current story and actions from StoryContext
  const { story: currentStory } = useStory();
  const { loadStoryForReadAloud: loadCurrentStoryForReadAloud } = useStoryActions();
  
  // State for tracking the current read-aloud session
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [readAloudStoryId, setReadAloudStoryId] = useState(null);
  const [readAloudStoryState, setReadAloudStoryState] = useState(null);

  // Keep a ref of the current read‑aloud story id so that "one‑time"
  // cleanup logic (in an empty‑deps useEffect) can still access the
  // latest value on unmount without having to re‑run the effect every
  // time the id changes.
  const readAloudStoryIdRef = useRef(null);
  
  // Use ref to always have access to latest state in callbacks
  const storyDataRef = useRef(null);

  // Audio service reference
  const audioService = useRef(null);
  
  // Track location changes for handling internal navigation
  const location = useLocation();
  
  // Create a ref to track when a story gets updated with new pages
  const storyUpdateHandlerRef = useRef(null);
  
  // Keep track of read-aloud story data in ref for access in callbacks
  // Use the standardized direct object format (no 'data' wrapper)
  useEffect(() => {
    debug('useEffect for readAloudStoryData triggered', {
      hasReadAloudStoryData: !!readAloudStoryData,
      storyDataId: readAloudStoryData?.id,
      previousStoryDataRefId: storyDataRef.current?.id,
      previousStateId: readAloudStoryState?.id
    });
    
    if (readAloudStoryData) {
      logger.debug('ReadAloud', `Updating storyDataRef with read-aloud story ${readAloudStoryData.id}`);
      
      // Store the story data directly - NEW ARCHITECTURE: direct object format
      storyDataRef.current = readAloudStoryData;
      
      // Also update state to trigger re-renders when read-aloud story changes
      debug('Setting readAloudStoryState from readAloudStoryData', {
        storyId: readAloudStoryData.id,
        hasPages: !!readAloudStoryData.pages,
        pagesCount: readAloudStoryData.pages?.length || 0
      });
      
      setReadAloudStoryState(readAloudStoryData);
    } else {
      debug('readAloudStoryData is null or undefined - not updating');
    }
  }, [readAloudStoryData]);

  // Keep the ref in sync with state – do this in its own tiny effect so we
  // are guaranteed to capture every change without causing re‑execution of
  // other effects.
  useEffect(() => {
    readAloudStoryIdRef.current = readAloudStoryId;
  }, [readAloudStoryId]);
  
  // ---------------------------------------------------------------------
  // Initialisation / teardown – this MUST run **exactly once** so that the
  // AudioService instance and global event listeners stay alive for the
  // lifetime of the application.  We therefore use an empty dependency
  // array and rely on refs for any values needed during cleanup.
  // ---------------------------------------------------------------------

  useEffect(() => {
    // Initialize only once
    if (!audioService.current) {
      logger.debug('ReadAloudContext', 'Creating new AudioService instance');
      audioService.current = new AudioService();
      
      // DEBUG: Check if Web Speech API is supported in dev mode only
      if (process.env.NODE_ENV === 'development') {
        logger.debug('ReadAloudContext', `Web Speech API supported: ${AudioService.isSupported()}`);
        logger.debug('ReadAloudContext', `Browser: ${AudioService.getBrowserInfo()}`);
      }
    }
    
    // Listen for audio service pause events
    const handleAudioPaused = (e) => {
      if (e.detail.success) {
        logger.debug('ReadAloud', 'Read aloud paused via AudioService event');
        setIsPlaying(false);
      }
    };
    
    document.addEventListener('audioServicePaused', handleAudioPaused);
    
    // Try to restore state from localStorage if available
    const savedState = localStorage.getItem('readAloudState');
    if (savedState) {
      try {
        const { storyId, pageIndex, playing } = JSON.parse(savedState);
        
        // Fetch the story data to restore it
        if (storyId) {
          // Set the read-aloud story ID first (this is needed for the WebSocket registration)
          setReadAloudStoryId(storyId);
          
          // Use StoryContext's method to load the story
          // Check if loadStoryFromContext exists before calling it
          if (loadStoryFromContext && typeof loadStoryFromContext === 'function') {
            loadStoryFromContext(storyId)
              .then(storyData => {
                if (!storyData) {
                  throw new Error('No story data returned');
                }
                
                // Update the current page index
              setCurrentPageIndex(pageIndex || 0);
              
              // Only start playing if it was playing before
              if (playing && storyData.pages && storyData.pages[pageIndex]?.text) {
                audioService.current.speak(storyData.pages[pageIndex].text, {
                  onEnd: () => setIsPlaying(false)
                });
                setIsPlaying(true);
              }
            })
            .catch(err => {
              logger.error('ReadAloudContext', 'Failed to restore read‑aloud state', err);
              // Clear saved state if restore fails
              localStorage.removeItem('readAloudState');
              // Reset read-aloud story ID since load failed
              setReadAloudStoryId(null);
            });
          } else {
            // If loadStoryFromContext isn't available, just clear the saved state
            logger.error('ReadAloudContext', 'loadStoryFromContext is not available');
            localStorage.removeItem('readAloudState');
          }
        }
      } catch (err) {
        logger.error('ReadAloudContext', 'Error parsing saved read‑aloud state', err);
        localStorage.removeItem('readAloudState');
      }
    }

    // Clean up on unmount – runs exactly **once** thanks to the empty deps
    // array.  We rely on the refs to access the latest values.
    return () => {
      document.removeEventListener('audioServicePaused', handleAudioPaused);

      // Unregister interest in the current story (if any)
      if (readAloudStoryIdRef.current) {
        services.websocket.unregisterRoomUsage(readAloudStoryIdRef.current, 'ReadAloudContext');
      }

      if (audioService.current?.isPlaying) {
        logger.debug('ReadAloud', 'Cleaning up AudioService on unmount');
        audioService.current.stop();
      }
    };
    // This cleanup runs **once**, when the provider itself unmounts (for
    // instance on full page refresh).  Using the ref ensures we reference
    // the latest story id without having to add it to this effect's deps.
    // (Note: We removed the previous cleanup block that depended on
    // readAloudStoryId to avoid re‑running the entire effect whenever that
    // state changes.)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // deliberately empty – see comment above
  
  // Track previous location for comparing navigation
  const prevLocationRef = useRef(location);
  
  // Handle internal route changes
  useEffect(() => {
    const prevPathname = prevLocationRef.current.pathname;
    const currentPathname = location.pathname;
    
    // Only take action for navigation OUTSIDE the story view
    // We want to keep playing when viewing a story
    const isFromStoryDetail = prevPathname.includes('/story/');
    const isToStoryDetail = currentPathname.includes('/story/');
    
    // If we're navigating away from a story view to a non-story view, we can consider pausing
    // But we never want to automatically pause when we're staying in or entering a story view
    if (!isToStoryDetail && isFromStoryDetail && isPlaying && audioService.current) {
      debug('Navigating away from story, but KEEPING audio playing across app');
      // We keep playing - that's the desired behavior!
      // The control bar will stay visible and keep playing
    }
    
    // Update the previous location reference
    prevLocationRef.current = location;
  }, [location, isPlaying]);
  
  // Effect to handle browser tab/window closing
  useEffect(() => {
    // Save the current state before page unload
    const handleBeforeUnload = () => {
      // If user is closing the page while playing audio, clean up
      if (isPlaying && audioService.current) {
        audioService.current.stop();
      }
    };
    
    // Add event listener for page unload
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    // Clean up
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isPlaying]);
  
  // Listen for force reset event (like logout)
  useEffect(() => {
    const handleForceReset = (event) => {
      logger.info('ReadAloudContext', 'Received force reset event:', event.detail);
      
      // Stop and unload any active read-aloud session
      if (audioService.current?.isPlaying) {
        audioService.current.stop();
      }
      
      // Clear local storage state
      localStorage.removeItem('readAloudState');
      
      // Unregister interest in the current story
      if (readAloudStoryId) {
        services.websocket.unregisterRoomUsage(readAloudStoryId, 'ReadAloudContext');
      }
      
      // Reset state
      setReadAloudStoryId(null);
      setReadAloudStoryState(null);
      storyDataRef.current = null;
      setIsPlaying(false);
      setCurrentPageIndex(0);
      
      logger.info('ReadAloudContext', 'All read-aloud state has been reset');
    };
    
    // Add event listener
    document.addEventListener('forceResetAllContexts', handleForceReset);
    
    // Cleanup on unmount
    return () => {
      document.removeEventListener('forceResetAllContexts', handleForceReset);
    };
  }, [readAloudStoryId]);


  /**
   * Start reading the current story at the specified page index
   * @param {number} startPageIndex - The page index to start reading from (optional)
   */
  const startReading = useCallback(async (startPageIndex = null) => {
    debug('startReading called', {
      startPageIndex,
      readAloudStoryId,
      hasReadAloudStoryData: !!readAloudStoryData,
      readAloudStoryDataId: readAloudStoryData?.id,
      storyDataRefId: storyDataRef.current?.id
    });
    
    // First check if we already have valid read-aloud story data
    let currentStory = readAloudStoryData;
    
    // Log the state of the read-aloud story data
    logger.debug('ReadAloudContext', `StartReading called with readAloudStoryData:`, {
      type: typeof currentStory,
      hasId: currentStory?.id ? `${currentStory.id} (${typeof currentStory.id})` : 'missing',
      title: currentStory?.title || 'missing',
      pagesCount: currentStory?.pages?.length || 0,
      rawData: JSON.stringify(currentStory || {}).substring(0, 200) + '...'
    });
    
    // If we don't have valid read-aloud story data, try to load it directly from StoryView's story ID
    if ((!currentStory || !currentStory.id || !currentStory.pages) && loadStoryFromContext) {
      // Use the read-aloud story ID if it's already set by a previous operation
      // This is different from the active story in StoryContext - it's the ID this context is tracking
      if (readAloudStoryId) {
        debug('No valid story data, trying to load using readAloudStoryId', {
          readAloudStoryId
        });
        
        logger.debug('ReadAloudContext', `Loading read-aloud story using readAloudStoryId: ${readAloudStoryId}`);
        try {
          // Call the StoryContext's loadStoryForReadAloud method to load the story specifically for read-aloud
          debug('Calling loadStoryFromContext', {
            readAloudStoryId,
            loadStoryFromContextType: typeof loadStoryFromContext
          });
          
          currentStory = await loadStoryFromContext(readAloudStoryId);
          
          debug('loadStoryFromContext result', {
            success: !!currentStory,
            loadedStoryId: currentStory?.id,
            hasPages: !!currentStory?.pages,
            pagesCount: currentStory?.pages?.length || 0
          });
          
          if (!currentStory) {
          logger.error('ReadAloudContext', 'Failed to load story from context: no data returned');
            throw new Error('Failed to load story for read-aloud');
          }
          logger.debug('ReadAloudContext', `Successfully loaded read-aloud story: ${currentStory.title}`);
        } catch (error) {
        logger.error('ReadAloudContext', 'Failed to load read‑aloud story', error);
        }
      } else {
        logger.error('ReadAloudContext', 'No readAloudStoryId set, cannot load story');
      }
    } else {
      debug('Using existing story data', {
        storyId: currentStory?.id,
        hasPages: !!currentStory?.pages,
        pagesCount: currentStory?.pages?.length || 0
      });
    }
    
    // Validate we have a proper story with pages
    if (!currentStory || !currentStory.id || !currentStory.pages || !audioService.current) {
      logger.error('ReadAloudContext', 'Cannot start reading: invalid story data or audio service not initialized', {
        hasStory: !!currentStory,
        hasId: currentStory?.id ? true : false,
        hasPages: currentStory?.pages ? true : false,
        hasAudioService: !!audioService.current
      });
      return;
    }
    
    // Use the provided page index or get it from StoryView's current index
    const pageToRead = startPageIndex !== null ? startPageIndex : currentPageIndex;
    
    logger.readAloud(`Starting to read story "${currentStory.title}" from page ${pageToRead + 1}`, true);
    
    // Register this story as being used by ReadAloudContext
    services.websocket.registerRoomUsage(currentStory.id, 'ReadAloudContext');
    
    // Set our internal state - use storyId from the currentStory object to ensure consistency
    const storyIdForState = currentStory.id;
    setReadAloudStoryId(storyIdForState);
    setCurrentPageIndex(pageToRead);
    setIsPlaying(true);

    // Save state to localStorage
    localStorage.setItem('readAloudState', JSON.stringify({
      storyId: storyIdForState,
      pageIndex: pageToRead,
      playing: true
    }));

    // Read the current page and continue to next pages
    readPageAndContinue(currentStory, pageToRead);
  }, [currentPageIndex, readAloudStoryId, loadStoryFromContext]);
  
  /**
   * Read current page and automatically proceed to the next page
   * Uses storyDataRef to always access the latest story data
   */
  const readPageAndContinue = useCallback((story, pageIndex) => {
    // NEW ARCHITECTURE: Direct object format (no 'data' wrapper)
    
    if (!story?.pages || !audioService.current) return;
    
    const currentPage = story.pages[pageIndex];
    if (!currentPage?.text) {
      logger.debug('ReadAloud', `Page ${pageIndex + 1} has no text to read`);
      setIsPlaying(false);
      return;
    }
    
    logger.readAloud(`Reading page ${pageIndex + 1} of story "${story.title}"`, true);
    
    // Prevent accidental restarts of the SAME utterance (e.g. when the user
    // navigates within the SPA but the ReadAloudContext stays mounted).
    // If the AudioService is already playing *and* the text it is currently
    // speaking is identical to the text we are about to read we simply bail
    // out – the existing narration can continue uninterrupted.
    if (audioService.current.isPlaying &&
        audioService.current.currentText === currentPage.text) {
      logger.debug('ReadAloud', 'Skipping speak() because identical utterance is already playing');
      return;
    }

    audioService.current.speak(currentPage.text, {
      onEnd: () => {
        // DEBUG: Log to detect whether onEnd is being triggered properly
        logger.debug('ReadAloud', `onEnd triggered for page ${pageIndex + 1}`);
        
        // We need to check if any new pages have been added while this page was being read
        
        // IMPORTANT: Use the ref to get the most up-to-date story state
        // The storyDataRef is updated from StoryContext, giving us the latest value
        // NEW ARCHITECTURE: Direct object format (no 'data' wrapper)
        let updatedStory = storyDataRef.current || story;
        
        // Log if we're seeing an updated story compared to the passed-in story
        if (updatedStory && updatedStory.pages && story && story.pages && 
            updatedStory.pages.length !== story.pages.length) {
          logger.debug('ReadAloud', `Using updated story with ${updatedStory.pages.length} pages from StoryContext (was ${story.pages.length})`);
        }
        
        const nextPageIndex = pageIndex + 1;
        
        // DEBUG: Before checking for next page, let's log state of all existing pages
        logger.debug('ReadAloud', `Checking for next page after page ${pageIndex + 1}`);
        logger.debug('ReadAloud', `Current playing state: ${isPlaying}`);
        logger.debug('ReadAloud', `Story "${updatedStory.title}" (ID: ${updatedStory.id}): ${updatedStory.pages?.length || 0} pages of ${updatedStory.total_pages || 'unknown'} total`);
        
        
        // First make sure pages are properly sorted by page_number
        // This is CRITICAL for consistent navigation
        if (updatedStory.pages && Array.isArray(updatedStory.pages)) {
          // Sort the pages array by page_number to ensure correct order
          updatedStory.pages.sort((a, b) => a.page_number - b.page_number);
          
          // DEBUG: Log only the number of pages and page numbers for clarity
          logger.debug('ReadAloud', `Pages after sorting: ${updatedStory.pages.length} pages, numbers: ${
            updatedStory.pages.map(p => p.page_number).join(', ')
          }`);
        }
        
        // Check if we have a next page with text using multiple approaches
        let hasNextPage = false;
        let nextPageData = null;
        
        // Approach 1: Check by array index
        if (nextPageIndex < updatedStory.pages.length && updatedStory.pages[nextPageIndex]?.text) {
          hasNextPage = true;
          nextPageData = updatedStory.pages[nextPageIndex];
          logger.debug('ReadAloud', `Found next page by array index: ${nextPageIndex}`);
        } 
        // Approach 2: Check by page_number field
        else {
          const pageByNumber = updatedStory.pages.find(p => p.page_number === nextPageIndex && p.text);
          if (pageByNumber) {
            hasNextPage = true;
            nextPageData = pageByNumber;
            logger.debug('ReadAloud', `Found next page by page_number: ${nextPageIndex}`);
          }
        }
        
        // DEBUG: Log the decision process
        // Simplified next page decision log
        if (hasNextPage) {
          logger.debug('ReadAloud', `Next page decision: Found next page ${nextPageIndex} (ID: ${nextPageData.id})`);
        } else {
          logger.debug('ReadAloud', `Next page decision: No next page found after page ${pageIndex}`);
        }
        
                
        if (hasNextPage) {
          logger.readAloud(`Found next page ${nextPageIndex + 1} with text. Continuing reading.`, true);
          
          // Update UI state to next page
          setCurrentPageIndex(nextPageIndex);
          
          // Update localStorage with new page
          localStorage.setItem('readAloudState', JSON.stringify({
            storyId: updatedStory.id,
            pageIndex: nextPageIndex,
            playing: true
          }));
          
          // Begin reading next page - using updatedStory
          readPageAndContinue(updatedStory, nextPageIndex);
        } else {
          logger.readAloud(`No next page found after page ${pageIndex + 1}. Stopping read-aloud.`, true);
          
          // Set paused state since we don't have a next page to read
          setIsPlaying(false);
          
          // Update localStorage to reflect stopped state
          localStorage.setItem('readAloudState', JSON.stringify({
            storyId: updatedStory.id,
            pageIndex: pageIndex,
            playing: false
          }));
          
          // For clean UI updates
          document.dispatchEvent(new CustomEvent('audioServicePaused', {
            detail: { success: true }
          }));
          
          // Reset pause state for clean resuming later
          if (audioService.current) {
            audioService.current.isPaused = false;
          }
        }
      }
    });
  }, [isPlaying]);

  /**
   * Unload the current story and stop reading
   */
  const unloadReading = useCallback(() => {
    if (audioService.current) {
      logger.readAloud('Stopping and unloading current story', true);
      audioService.current.stop();
    }

    // Remove persisted state
    localStorage.removeItem('readAloudState');

    // Unregister interest in the current story
    if (readAloudStoryId) {
      services.websocket.unregisterRoomUsage(readAloudStoryId, 'ReadAloudContext');
    }

    setReadAloudStoryId(null);
    setReadAloudStoryState(null);
    // Also clear the ref to ensure it's not used as fallback
    storyDataRef.current = null;
    setIsPlaying(false);
    setCurrentPageIndex(0);
  }, [readAloudStoryId]);

  /**
   * Toggle between play and pause states
   */
  const togglePlayPause = useCallback(() => {
    // Get the current read-aloud story from the ref
    // Direct object format (no 'data' wrapper)
    const storyToRead = storyDataRef.current;
    
    if (!audioService.current || !storyToRead || !storyToRead.id) {
      logger.error('ReadAloudContext', 'Cannot toggle play/pause: no active story or audio service');
      return;
    }

    logger.debug('ReadAloud', `Toggling play/pause. Current state: ${isPlaying ? 'playing' : 'paused'}`);
    
    // New state is the opposite of current state
    const newPlayingState = !isPlaying;
    
    if (isPlaying) {
      // If currently playing, pause the audio
      const pauseSuccess = audioService.current.pause();
      
      // Immediately update UI state even before the event arrives
      // This makes the button appear responsive immediately
      if (pauseSuccess) {
        setIsPlaying(false);
      }
      
      // The audioServicePaused event will also update state for consistency
    } else {
      // If we're hitting play from a paused state
      
      // If paused during active reading, resume from current position
      if (audioService.current.isPaused) {
        // Just resume the current utterance if we're in the middle of a page
        const resumeSuccess = audioService.current.resume();
        
        if (resumeSuccess) {
          setIsPlaying(true);
        } else {
          logger.debug('ReadAloud', 'Failed to resume - starting from beginning of page instead');
          // If resume fails, restart the current page
          readPageAndContinue(currentStory, currentPageIndex);
        }
      } else {
        // We weren't paused mid-utterance, so start from beginning of current page
        logger.debug('ReadAloud', `Starting from beginning of page ${currentPageIndex + 1}`);
        
        // Important: Explicitly set playing state to true BEFORE starting to read
        // This ensures the UI updates immediately
        setIsPlaying(true);
        
        // Now restart from the current page
        readPageAndContinue(currentStory, currentPageIndex);
      }
    }
    
    // Persist state to localStorage
    localStorage.setItem('readAloudState', JSON.stringify({
      storyId: storyToRead.id,
      pageIndex: currentPageIndex,
      playing: newPlayingState
    }));
  }, [isPlaying, currentPageIndex, readPageAndContinue]);

  /**
   * Check if reading is currently active for a specific story
   * @param {string|number} storyId - The ID of the story to check
   * @returns {boolean} True if reading is active for this story
   */
  const isReadingActive = useCallback((storyId) => {
    return readAloudStoryId === storyId;
  }, [readAloudStoryId]);
  
  /**
   * Load a story for read-aloud that might not be the active story in StoryContext
   * @param {string|number} storyId - The ID of the story to load
   * @returns {Promise<boolean>} - True if story was loaded successfully
   */
  const loadStoryForReadAloud = useCallback(async (storyId) => {
      debug('loadStoryForReadAloud called', {
      storyId,
      currentReadAloudStoryId: readAloudStoryId,
      hasCurrentStoryState: !!readAloudStoryState
    });
    
    if (!storyId) {
      logger.error('ReadAloudContext', 'Cannot load story: No story ID provided');
      logger.error('ReadAloud', 'Cannot load story: No story ID provided');
      return false;
    }
    
    try {
      logger.debug('ReadAloud', `Loading story ${storyId} for read-aloud using StoryContext`);
      
      // Log the current state of storyDataRef and readAloudStoryState before loading
      debug('State before loading', {
        readAloudStoryId: readAloudStoryId,
        hasReadAloudStoryState: !!readAloudStoryState,
        readAloudStoryStateId: readAloudStoryState?.id,
        hasStoryDataRef: !!storyDataRef.current,
        storyDataRefId: storyDataRef.current?.id
      });
      
      // Set the read-aloud story ID first (needed for tracking in ReadAloudContext)
      debug('Setting readAloudStoryId', {
        from: readAloudStoryId,
        to: storyId
      });
      
      setReadAloudStoryId(storyId);
      
      // Use StoryContext's method to load the story
      // This handles API fetch, WebSocket registration, and error handling
      debug('Calling loadStoryFromContext', {
        storyId,
        loadStoryFromContextType: typeof loadStoryFromContext
      });
      
      const storyData = await loadStoryFromContext(storyId);
      
      // Log the received story data
      debug('loadStoryFromContext result', {
        hasStoryData: !!storyData,
        storyDataId: storyData?.id,
        storyDataTitle: storyData?.title,
        storyDataPagesCount: storyData?.pages?.length || 0,
        dataType: typeof storyData,
        firstPageText: storyData?.pages?.[0]?.text?.substring(0, 20)
      });
      
      logger.debug('ReadAloud', `loadStoryFromContext returned:`, {
        hasStoryData: !!storyData,
        storyDataId: storyData?.id,
        storyDataTitle: storyData?.title,
        storyDataPagesCount: storyData?.pages?.length || 0
      });
      
      if (!storyData) {
        logger.error('ReadAloudContext', 'No story data returned from loadStoryFromContext');
        logger.error('ReadAloud', `Failed to load story ${storyId}: No data returned from StoryContext`);
        setReadAloudStoryId(null);
        return false;
      }
      
      // Reset to the first page
      setCurrentPageIndex(0);
      
      // Update the read-aloud story state
      debug('Setting readAloudStoryState', {
        currentState: readAloudStoryState?.id,
        newStateId: storyData.id
      });
      
      // We're supposed to update readAloudStoryState here, but it's not directly exposed
      // Instead, we rely on the useEffect that watches readAloudStoryData to update it
      // Let's log this observation
      debug('Note: Relying on useEffect to update readAloudStoryState from readAloudStoryData');
      
      // CRITICAL: Directly update the ref immediately for startReading to use
      // This ensures consistent state across async boundaries
      const prevRefId = storyDataRef.current?.id;
      storyDataRef.current = storyData;
      
      debug('Updated storyDataRef', {
        from: prevRefId,
        to: storyData.id,
        pagesCount: storyData.pages?.length || 0
      });
      
      // Log the state after loading, before returning
      debug('State after loading', {
        readAloudStoryId: readAloudStoryId, // Will still show old value due to async state
        hasReadAloudStoryState: !!readAloudStoryState,
        readAloudStoryStateId: readAloudStoryState?.id,
        hasUpdatedStoryDataRef: !!storyDataRef.current,
        storyDataRefId: storyDataRef.current?.id,
        storyDataRefTitle: storyDataRef.current?.title
      });
      
      logger.debug('ReadAloud', `Successfully loaded story "${storyData.title}" (ID: ${storyId}) for read-aloud`);
      return true;
    } catch (error) {
      logger.error('ReadAloudContext', 'Error loading story for read‑aloud', error);
      logger.error('ReadAloud', `Error loading story ${storyId} for read-aloud:`, error);
      setReadAloudStoryId(null);
      return false;
    }
  }, [loadStoryFromContext, readAloudStoryId, readAloudStoryState, setReadAloudStoryId, setCurrentPageIndex]);

  // Set up a listener for story and page updates via WebSocket
  useEffect(() => {
    // Only run this if we have an read-aloud story ID
    if (!readAloudStoryId) return;
    

    
    // Handler to update our story state when it changes via WebSocket
    const handleStoryUpdate = (event) => {
      // DEBUG: More extensive logging for test debugging
      debug('storyUpdate event received', {
        eventStoryId: event.detail?.storyId,
        readAloudStoryId: readAloudStoryId,
        match: event.detail?.storyId === readAloudStoryId,
        hasStory: !!event.detail?.story,
        eventDetails: JSON.stringify(event.detail).substring(0, 100),
        // Add more detailed information about what's in the event
        storyUpdateEventTitle: event.detail?.story?.title,
        storyHasPages: !!event.detail?.story?.pages
      });
      
      if (event.detail && event.detail.storyId === readAloudStoryId) {
        // Always update our story with the latest data, regardless of navigation state
        const rawStory = event.detail.story;
        
        // Verify we have a valid story with pages from the WebSocket
        if (!rawStory || !rawStory.pages || !Array.isArray(rawStory.pages)) {
          debug('Invalid story data in storyUpdate', {
            hasStory: !!rawStory,
            hasPages: !!rawStory?.pages,
            isArray: Array.isArray(rawStory?.pages)
          });
          return;
        }
        
        // Get current story from ref for comparison
        const currentStory = storyDataRef.current;
        
        // Check if we have more pages now than before
        const hadPagesBeforeUpdate = currentStory?.pages ? currentStory.pages.length : 0;
        const hasMorePages = rawStory.pages.length > hadPagesBeforeUpdate;
        
        // Log only the most important information
        if (hasMorePages) {
      debug(`Story update: ${hadPagesBeforeUpdate} → ${rawStory.pages.length} pages`);
        }
        
        // Check if title changed or we got a title update
        const titleChanged = currentStory && rawStory.title !== currentStory.title;
        
        // Update storyDataRef with the latest story data
        // NEW ARCHITECTURE: Use direct object format (no 'data' wrapper)
        storyDataRef.current = rawStory;
        debug('Updating storyDataRef and state', {
          oldTitle: currentStory?.title,
          newTitle: rawStory?.title,
          updatedStory: JSON.stringify(rawStory).substring(0, 100)
        });
        
        // Also update state to trigger re-renders when story data changes
        // This ensures components using our context see updated title and other changes
        setReadAloudStoryState(rawStory);
        
        // We don't want to auto-continue reading here when new pages come in
        // Instead, we'll let the audio service's onEnd callback check for new pages
        // This ensures we only continue after the current page finishes reading
      }
    };
    
    // Handler for individual page updates - needed when only a single page is updated
    // rather than the whole story object
    const handlePageUpdate = (event) => {
      if (!event.detail || event.detail.storyId !== readAloudStoryId) return;
      
      logger.readAloud(`Detected page update for page ${event.detail.pageNumber} of story ${event.detail.storyId}`);
      
      const updatedPage = event.detail.page;
      const pageNumber = event.detail.pageNumber;
      
      // DEBUG: Log current state before update
      logger.debug('ReadAloud', `Before page update, current playing state: ${isPlaying}`);
      logger.debug('ReadAloud', `Before page update, currentPageIndex: ${currentPageIndex}`);
      
      // Get current story data from StoryContext
      const currentStory = storyDataRef.current;
      if (currentStory?.pages) {
        logger.debug('ReadAloud', `Before page update, total pages: ${currentStory.pages.length}`);
      }
      
      // Since we're using StoryContext as our source of truth, we just need to 
      // monitor for conditions where we should start reading automatically
      
      // Check if we can determine if this is a new page
      let isNewPage = false;
      if (currentStory?.pages && updatedPage && updatedPage.id) {
        isNewPage = !currentStory.pages.some(p => p.id === updatedPage.id);
        if (isNewPage) {
          logger.readAloud(`Detected new page ${updatedPage.page_number} (ID: ${updatedPage.id})`);
        }
      }
      
      // Check if we're currently reading a page
      let currentlyReadingPage = null;
      if (currentStory?.pages && currentPageIndex >= 0 && currentPageIndex < currentStory.pages.length) {
        currentlyReadingPage = currentStory.pages[currentPageIndex];
        logger.debug('ReadAloud', `Currently reading: page ${currentlyReadingPage.page_number} (index: ${currentPageIndex}, id: ${currentlyReadingPage.id})`);
      } else {
        logger.debug('ReadAloud', 'Currently reading: No current page');
      }
      
      // Check if the new/updated page is the next sequential page after what we're currently reading
      const isNextSequentialPage = isNewPage && 
                                 currentlyReadingPage && 
                                 updatedPage && 
                                 updatedPage.page_number === currentlyReadingPage.page_number + 1;
                                 
      logger.debug('ReadAloud', `Is this a new sequential page? ${isNextSequentialPage}`);
      
      // Check if we need to auto-start reading the new page
      const shouldAutoStartNewPage = isNewPage && 
                                    updatedPage && 
                                    updatedPage.text && // Has text to read
                                    !isPlaying && // Not currently playing
                                    currentlyReadingPage && // We have a current page
                                    updatedPage.page_number === currentPageIndex + 1; // Is the next sequential page
                                    
      logger.debug('ReadAloud', `Should auto-start reading new page? ${shouldAutoStartNewPage}`);
      
      // If we should auto-start reading the new page, do it
      if (shouldAutoStartNewPage && updatedPage && typeof updatedPage.page_number === 'number') {
        logger.readAloud(`Auto-starting narration of new page ${updatedPage.page_number}`, true);
        
        // First, update the current page index to the new page
        setCurrentPageIndex(updatedPage.page_number);
        
        // Small delay to ensure state is updated before starting to read
        setTimeout(() => {
          // Start reading the new page
          setIsPlaying(true);
          // Use the story from StoryContext as the latest state
          const latestStory = storyDataRef.current;
          readPageAndContinue(latestStory, updatedPage.page_number);
        }, 100);
      } else {
        // The read-aloud feature will naturally continue to the next page
        // when the current page finishes reading, based on this updated story state
        // We DON'T want to auto-navigate here, as that could interrupt reading
      }
    };
    
    // Handle story_left messages from the server
    const handleStoryLeft = (event) => {
      if (!event.detail || event.detail.storyId !== readAloudStoryId) return;
      
      logger.readAloud(`Detected story_left event for story ${event.detail.storyId}`);
      
      // If the story we're reading has been left, we should unload reading
      if (isPlaying) {
        logger.readAloud('Story room was closed by the server. Stopping playback.', true);
        unloadReading();
      }
    };
    
    // Register for story and page updates
    document.addEventListener('storyUpdate', handleStoryUpdate);
    document.addEventListener('pageUpdate', handlePageUpdate);
    document.addEventListener('storyLeft', handleStoryLeft);
    
    // DEBUG: Log registration of WebSocket event handlers (only in dev)
    if (process.env.NODE_ENV === 'development') {
      logger.debug('ReadAloudContext', `Registered WebSocket event handlers for story: ${readAloudStoryId}`);
    }
    
    // Title update handler - specifically for when only the title is updated
    const handleTitleUpdate = (event) => {
      if (!event.detail || event.detail.storyId !== readAloudStoryId) return;
      
      debug(`Detected title update for story ${event.detail.storyId}: "${event.detail.title}"`);
    };
    
    // Handle page navigation events from StoryView
    const handlePageNavigation = (event) => {
      if (!event.detail || event.detail.storyId !== readAloudStoryId) return;
      
      const { pageIndex, page, autoNavigated } = event.detail;
      logger.readAloud(`Detected page navigation to page ${pageIndex + 1} (auto: ${!!autoNavigated})`);
      
      // If we're currently playing audio, we don't want to interrupt
      // We only update our internal state when appropriate
      const shouldUpdateIndex = !isPlaying || (autoNavigated && pageIndex === currentPageIndex + 1);
      
      // Only update current page index if we're not actively reading
      // or if this is sequential auto-navigation to the next page
      if (shouldUpdateIndex) {
        logger.debug('ReadAloud', `Updating current page index to ${pageIndex} (was ${currentPageIndex})`);
        setCurrentPageIndex(pageIndex);
      } else {
        logger.debug('ReadAloud', `Not updating page index during active reading. StoryView: ${pageIndex}, ReadAloud: ${currentPageIndex}`);
      }
      
      // No need to maintain our own copy of the story - StoryContext handles that
      
      // Only update localStorage if we're updating the index
      if (shouldUpdateIndex) {
        localStorage.setItem('readAloudState', JSON.stringify({
          storyId: readAloudStoryId,
          pageIndex: pageIndex,
          playing: isPlaying
        }));
      }
    };
    
    // Save reference for cleanup
    storyUpdateHandlerRef.current = {
      story: handleStoryUpdate,
      page: handlePageUpdate,
      title: handleTitleUpdate,
      navigation: handlePageNavigation,
      storyLeft: handleStoryLeft
    };
    
    // Register for all event types
    document.addEventListener('storyUpdate', handleStoryUpdate);
    document.addEventListener('pageUpdate', handlePageUpdate);
    document.addEventListener('titleUpdate', handleTitleUpdate);
    document.addEventListener('pageNavigation', handlePageNavigation);
    document.addEventListener('storyLeft', handleStoryLeft);
    
    return () => {
      if (storyUpdateHandlerRef.current) {
        document.removeEventListener('storyUpdate', storyUpdateHandlerRef.current.story);
        document.removeEventListener('pageUpdate', storyUpdateHandlerRef.current.page);
        document.removeEventListener('titleUpdate', storyUpdateHandlerRef.current.title);
        document.removeEventListener('pageNavigation', storyUpdateHandlerRef.current.navigation);
        document.removeEventListener('storyLeft', storyUpdateHandlerRef.current.storyLeft);
      }
      
      // Unregister interest in the story when this effect cleanup runs
      services.websocket.unregisterRoomUsage(readAloudStoryId, 'ReadAloudContext');
    };
  }, [readAloudStoryId, currentPageIndex, isPlaying, readPageAndContinue, unloadReading]);
  
  // Get the active story data using the standardized format
  // Always expect and use the story.data format across the entire application
  // Use the state variable for rendering, falling back to the ref if needed
  const readAloudStory = readAloudStoryState || storyDataRef.current;

  /**
   * High-level function to start reading the current story in the StoryContext
   * This is the main entry point for components like ReadAloudButton
   * @param {number} startPageIndex - Optional page index to start reading from
   */
  const startReadingCurrentStory = useCallback(async (startPageIndex = null) => {
    debug('startReadingCurrentStory called', {
      startPageIndex,
      currentReadAloudStoryId: readAloudStoryId,
      hasCurrentStoryFromContext: !!currentStory,
      currentStoryId: currentStory?.id,
      hasStoryDataRef: !!storyDataRef.current,
      storyDataRefId: storyDataRef.current?.id,
      readAloudStoryState: readAloudStoryState?.id
    });
    
    logger.readAloud('Starting read-aloud for current story from UI', true);
    
    // First try to get the current story from StoryContext
    const currentStoryData = currentStory;
    
    debug('Current story data from StoryContext', {
      hasData: !!currentStoryData,
      id: currentStoryData?.id,
      title: currentStoryData?.title,
      pagesCount: currentStoryData?.pages?.length || 0
    });
    
    if (!currentStoryData || !currentStoryData.id) {
      logger.error('ReadAloudContext', 'No active story in StoryContext to read');
      return;
    }

    try {
      // Get the story ID we're going to work with
      const storyIdToUse = currentStoryData.id;
      
      // If the read-aloud context doesn't have this story loaded yet, load it
      if (readAloudStoryId !== storyIdToUse) {
        debug('Loading story for read‑aloud via loadStoryForReadAloud', {
          storyId: storyIdToUse
        });
        
        // Use our method to load the story specifically for read-aloud
        // This should properly update both the state and the ref
        const loadSuccess = await loadStoryForReadAloud(storyIdToUse);
        
        debug('Load result', {
          success: loadSuccess,
          newReadAloudStoryId: readAloudStoryId, // This might still be null due to async state update
          hasReadAloudStoryState: !!readAloudStoryState,
          storyDataRefUpdated: storyDataRef.current?.id === storyIdToUse
        });
        
        if (!loadSuccess) {
          logger.error('ReadAloudContext', 'Failed to load story for read‑aloud');
          return;
        }
      } else {
        debug('Story already loaded in read‑aloud context', {
          storyId: readAloudStoryId
        });
      }
      
      // IMPORTANT: We need to make sure we're using consistent data between
      // loadStoryForReadAloud and startReading, so let's directly read from ref
      const storyFromRef = storyDataRef.current;
      
      debug('About to call startReading, checking story from ref', {
        hasRefData: !!storyFromRef,
        refStoryId: storyFromRef?.id,
        readAloudStoryId,  // State might still not be updated due to async state updates
        hasReadAloudStoryState: !!readAloudStoryState,
        storyDataRefId: storyDataRef.current?.id,
        startingPageIndex: startPageIndex,
      });
      
      // If we have valid ref data, we can safely start reading
      if (storyFromRef && storyFromRef.id && storyFromRef.pages) {
        // Set state directly for immediate consistency
        setReadAloudStoryId(storyFromRef.id);
        
        // Now start reading using the story ref data
        // We'll use readPageAndContinue directly to bypass the readAloudStoryId check
        const pageToRead = startPageIndex !== null ? startPageIndex : currentPageIndex;
        
        services.websocket.registerRoomUsage(storyFromRef.id, 'ReadAloudContext');
        setCurrentPageIndex(pageToRead);
        setIsPlaying(true);
        
        // Save state to localStorage
        localStorage.setItem('readAloudState', JSON.stringify({
          storyId: storyFromRef.id,
          pageIndex: pageToRead,
          playing: true
        }));
        
        // Start reading
        readPageAndContinue(storyFromRef, pageToRead);
        
        logger.readAloud(`Starting to read story "${storyFromRef.title}" from page ${pageToRead + 1}`, true);
      } else {
        logger.error('ReadAloudContext', 'Unable to start reading – no valid story in ref data');
      }
    } catch (error) {
      logger.error('ReadAloudContext', 'Error in startReadingCurrentStory', error);
    }
  }, [currentStory, currentPageIndex, readAloudStoryId, readAloudStoryState, loadStoryForReadAloud, readPageAndContinue, setReadAloudStoryId, setCurrentPageIndex, setIsPlaying]);

  const value = {
    isPlaying,
    currentPageIndex,
    readAloudStoryId,
    readAloudStory, // Provide the story data in the standardized format
    startReading,
    startReadingCurrentStory, // High-level method for reading the current story
    unloadReading,
    togglePlayPause,
    isReadingActive,
    loadStoryForReadAloud // Our method that uses StoryContext
  };

  return (
    <ReadAloudContext.Provider value={value}>
      {children}
    </ReadAloudContext.Provider>
  );
};